import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import type { AxiosRequestConfig } from 'axios';
import { withIdempotencyKey } from './apiClient';
import { useOperationKey } from './operationKey';

export interface IdempotentMutationOptions<TData, TBody> {
  onSuccess?: (data: TData, body: TBody) => void;
  onError?: (error: unknown, body: TBody) => void;
}

/**
 * A mutation that is safe to retry.
 *
 * The key's lifecycle lives here rather than in the screens, so no call site has to remember when
 * to reuse one and when to mint a fresh one — get that wrong in one place and you either charge the
 * customer twice or refuse a legitimate second sale of the same basket.
 *
 * Each hook instance owns one intent. Checkout gets its own, a later payment gets another; they
 * never share, which is what stops the server seeing a key arrive on a second endpoint and refusing
 * it as a reuse.
 */
export function useIdempotentMutation<TData, TBody>(
  send: (body: TBody, config: AxiosRequestConfig) => Promise<TData>,
  options: IdempotentMutationOptions<TData, TBody> = {},
): UseMutationResult<TData, unknown, TBody> {
  const key = useOperationKey();

  return useMutation<TData, unknown, TBody>({
    mutationFn: (body: TBody) => send(body, withIdempotencyKey(key.next(body))),
    onSuccess: (data, body) => {
      key.settle('SUCCESS');
      options.onSuccess?.(data, body);
    },
    onError: (error, body) => {
      // A dropped connection keeps the key; a refusal releases it.
      key.settle(key.classify(error));
      options.onError?.(error, body);
    },
  });
}
